import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';
import { mapSavedReport, type SavedReport } from './useReports';

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const pinKeys = {
  all: ['report-pins'] as const,
  list: () => [...pinKeys.all, 'list'] as const,
  ids: () => [...pinKeys.all, 'ids'] as const,
};

// ─── Hooks ─────────────────────────────────────────────────────────────────

/**
 * Fetch the current user's pinned reports (full records, runnable directly).
 * Used by the dashboard "My Reports" card.
 */
export function usePinnedReports(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pinKeys.list(),
    queryFn: async (): Promise<SavedReport[]> => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/reports/pinned');
      return (response.data.data ?? []).map(mapSavedReport);
    },
    enabled: options?.enabled !== false,
  });
}

/**
 * Fetch the set of saved-report ids the current user has pinned. Used to badge
 * the Reports list and toggle the Pin/Unpin action.
 */
export function usePinnedReportIds(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pinKeys.ids(),
    queryFn: async (): Promise<string[]> => {
      const response = await apiClient.get<ApiResponse<string[]>>('/reports/pinned/ids');
      return response.data.data ?? [];
    },
    enabled: options?.enabled !== false,
  });
}

/** Pin a saved report to the current user's dashboard. */
export function usePinReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reportId: string) => {
      await apiClient.post(`/reports/saved/${reportId}/pin`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pinKeys.all });
    },
  });
}

/** Remove the current user's pin for a saved report. */
export function useUnpinReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reportId: string) => {
      await apiClient.delete(`/reports/saved/${reportId}/pin`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pinKeys.all });
    },
  });
}
